import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Badge
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  RecordVoiceOver as SuggestionIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { getProductById, getProductReviews } from '../../services/storeService';
import { useCart } from '../../contexts/CartContext';
import { CartDrawer } from '../../components/store';
import ProductImageGallery from '../../components/store/ProductImageGallery';
// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';
import { Review } from '../../types/store';
import { useScrollToTopOnMount } from '../../hooks/useScrollToTop';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import ProductSuggestionModal from '../../components/store/ProductSuggestionModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<InventoryItem | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const { cart, addToCart, isInCart } = useCart();
  const { settings: storeSettings } = useStoreSettings();

  // التمرير إلى أعلى الصفحة عند تحميل المكون
  useScrollToTopOnMount();

  useEffect(() => {

    const fetchProductDetails = async () => {
      if (!productId) {
        setError('معرف المنتج غير صالح');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching product details for ID:', productId);

        // جلب تفاصيل المنتج
        const productData = await getProductById(productId);
        console.log('Product data received:', productData);
        setProduct(productData);

        // جلب المراجعات
        try {
          const reviewsData = await getProductReviews(productId);
          setReviews(reviewsData);
        } catch (reviewErr) {
          console.error('Error fetching product reviews:', reviewErr);
          // لا نعرض خطأ للمستخدم إذا فشل جلب المراجعات فقط
          setReviews([]);
        }

        setError(null);
      } catch (err: any) {
        console.error('Error fetching product details:', err);
        const errorMessage = err?.message || 'حدث خطأ أثناء تحميل تفاصيل المنتج';
        setError(errorMessage === 'Product not found' ? 'لم يتم العثور على المنتج' : 'حدث خطأ أثناء تحميل تفاصيل المنتج');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && product && newQuantity <= product.quantity) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      setCartOpen(true);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'لم يتم العثور على المنتج'}
        </Alert>
        <Button
          component={RouterLink}
          to="/store"
          variant="contained"
        >
          العودة إلى المتجر
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component={RouterLink}
              to="/"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              الرئيسية
            </Link>
            <Link
              component={RouterLink}
              to="/store"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <StoreIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              المتجر
            </Link>
            <Typography color="text.primary">{product.name}</Typography>
          </Breadcrumbs>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={4}>
            {/* صور المنتج */}
            <Grid item xs={12} md={6}>
              <ProductImageGallery
                mainImage={product.imageUrl || product.image}
                additionalImages={product.additionalImages}
                productName={product.name}
              />
            </Grid>

            {/* تفاصيل المنتج */}
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.name}
              </Typography>

              <Typography variant="h5" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                {product.sellingPrice.toLocaleString('ar-EG')} ج.م
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body1" sx={{ mb: 3 }}>
                {product.description || 'لا يوجد وصف متاح لهذا المنتج.'}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  الفئة: {product.category}
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  رمز المنتج: {product.sku}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mr: 1 }}>
                    الحالة:
                  </Typography>
                  {product.quantity > 0 ? (
                    <Chip
                      label={product.quantity > 5 ? 'متوفر' : `متبقي ${product.quantity}`}
                      color={product.quantity > 5 ? 'success' : 'warning'}
                      size="small"
                    />
                  ) : (
                    <Chip label="غير متوفر" color="error" size="small" />
                  )}
                </Box>
              </Box>

              {product.quantity > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mr: 2 }}>
                      الكمية:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <TextField
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value)) {
                          handleQuantityChange(value);
                        }
                      }}
                      inputProps={{
                        min: 1,
                        max: product.quantity,
                        style: { textAlign: 'center' }
                      }}
                      sx={{ width: 60, mx: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= product.quantity}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleAddToCart}
                    disabled={isInCart(product.id)}
                    fullWidth
                  >
                    {isInCart(product.id) ? 'تمت الإضافة إلى السلة' : 'أضف إلى السلة'}
                  </Button>

                  {/* زر اقتراح سعر أقل */}
                  {storeSettings?.productSuggestion?.isEnabled && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<SuggestionIcon />}
                      onClick={() => setSuggestionModalOpen(true)}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {storeSettings.productSuggestion.buttonText}
                    </Button>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="product tabs"
            centered
          >
            <Tab label="التفاصيل" id="product-tab-0" />
            <Tab label="المراجعات" id="product-tab-1" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="body1">
              {product.description || 'لا توجد تفاصيل إضافية لهذا المنتج.'}
            </Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <Box key={review.id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {review.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {new Date(review.createdAt).toLocaleDateString('ar-EG')}
                  </Typography>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    {[...Array(5)].map((_, i) => (
                      <span key={i} style={{ color: i < review.rating ? '#FFD700' : '#e0e0e0' }}>★</span>
                    ))}
                  </Box>
                  <Typography variant="body1">
                    {review.comment}
                  </Typography>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))
            ) : (
              <Typography variant="body1">
                لا توجد مراجعات لهذا المنتج حتى الآن.
              </Typography>
            )}
          </TabPanel>
        </Paper>
      </Container>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />

      {/* نافذة اقتراح سعر أقل */}
      {product && storeSettings?.productSuggestion && (
        <ProductSuggestionModal
          open={suggestionModalOpen}
          onClose={() => setSuggestionModalOpen(false)}
          product={product}
          settings={storeSettings.productSuggestion}
        />
      )}
    </>
  );
};

export default ProductDetailPage;
